import React, { useState, useRef, useEffect } from 'react';
import { Building2, ChevronDown, Check, Search } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../store';
import type { Practice } from '../../types';

export default function PracticeSwitcher() {
  const { currentPractice, assignedPractices, setCurrentPractice } = useAuthStore();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = assignedPractices.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (practice: Practice) => {
    setCurrentPractice(practice);
    setOpen(false);
    setSearch('');
    // Wave 3c (§2) — soft refresh: invalidate every react-query cache so practice-scoped pages
    // refetch under the new practice context WITHOUT a full reload (which would blow away the context
    // + burn bootstrap requests). Matches ModeToggle. The new X-Practice-ID is sent on the refetches.
    queryClient.invalidateQueries();
  };

  // Render whenever there are practices available to pick — even if none is
  // currently selected. Otherwise a fresh platform_admin who has never picked
  // a practice sees the empty-state message "use the switcher" but no switcher.
  if (assignedPractices.length === 0) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={currentPractice ? `Practice: ${currentPractice.name}. Click to switch.` : 'Select a practice'}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
          currentPractice ? '' : 'ring-2 ring-amber-300 bg-amber-50'
        }`}
      >
        <Building2 className={`w-4 h-4 flex-shrink-0 ${currentPractice ? 'text-gray-500' : 'text-amber-700'}`} />
        <span className={`text-sm font-medium max-w-[160px] truncate ${
          currentPractice ? 'text-gray-700' : 'text-amber-900'
        }`}>
          {currentPractice?.name || 'Select practice…'}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-200 z-50">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search practices..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#C94B1F]"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-xs text-gray-400 text-center">No practices found</p>
            ) : (
              filtered.map(practice => (
                <button
                  key={practice.practice_id}
                  onClick={() => handleSelect(practice)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="w-7 h-7 rounded-lg bg-[#F4F1EE] flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-3.5 h-3.5 text-[#C94B1F]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{practice.name}</p>
                    <p className="text-xs text-gray-400 capitalize">{practice.specialty}</p>
                  </div>
                  {practice.practice_id === currentPractice?.practice_id && (
                    <Check className="w-4 h-4 text-[#C94B1F] flex-shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
          <div className="px-3 py-2 border-t border-gray-100">
            <p className="text-xs text-gray-400">{assignedPractices.length} practice{assignedPractices.length !== 1 ? 's' : ''} assigned</p>
          </div>
        </div>
      )}
    </div>
  );
}
